import { Component, inject, output, ViewChild } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import {
  CancellationDocumentType,
  getCancellationDocumentTypeLabel,
} from 'src/app/_enums/cancellation/cancellation-document-type.enum';
import { isCancellationFlowOver } from 'src/app/_enums/cancellation/cancellation-flow-status.enum';
import { getApiErrorMessage } from 'src/app/_helpers/api-error.helpers';
import { fmtDate } from 'src/app/_helpers/utils';
import { CancellationService } from 'src/app/_services/cancellation.service';
import { UserAuthService } from 'src/app/_services/user-auth.service';
import {
  CancellationFlowDetailsResponse,
  CancellationFlowDocument,
} from 'src/app/_types/cancellation.types';
import { ActionModalComponent } from 'src/app/shared/components/action-modal/action-modal.component';
import { PdfViewerModalComponent } from 'src/app/shared/components/pdf-viewer-modal/pdf-viewer-modal.component';
import { IconsModule } from 'src/app/shared/icons/icons.module';
import { CardComponent } from 'src/app/shared/ui-kit/card/card.component';
import { ExpansionPanelComponent } from 'src/app/shared/ui-kit/expansion-panel/expansion-panel.component';

import { CancellationDetailsService } from './cancellation-details.service';

/** The design's short date on a document row: `15/04/26`. */
const SHORT_DATE: Intl.DateTimeFormatOptions = {
  day: '2-digit',
  month: '2-digit',
  year: '2-digit',
};

/**
 * « Documents »: every letter filed against the cancellation, newest first as
 * the aggregate orders them — a regenerated letter therefore sits above the
 * one it replaces, both kept.
 */
@Component({
  selector: 'app-cancellation-documents',
  standalone: true,
  imports: [
    ActionModalComponent,
    CardComponent,
    ExpansionPanelComponent,
    IconsModule,
    PdfViewerModalComponent,
  ],
  template: `
    <app-expansion-panel title="Documents" i18n-title>
      <button
        expansionPanelHeaderActions
        type="button"
        class="lty-btn lty-btn-white lty-btn-sm"
        [hidden]="!editable"
        (click)="onAdd()"
        i18n
      >
        Ajouter
      </button>
      @if (documents.length) {
        <app-card class="mt-8 d-block">
          <div cardRows class="card__rows">
            @for (document of documents; track document.documentId) {
              <div class="document">
                <app-file-lines-icon
                  height="16px"
                  width="16px"
                  color="var(--gray-950)"
                />
                @if (document.url) {
                  <span
                    class="document__name document__name--open clickable"
                    role="button"
                    tabindex="0"
                    (click)="open(document)"
                    (keydown.enter)="open(document)"
                    (keydown.space)="open(document)"
                    >{{ document.name }}</span
                  >
                } @else {
                  <span class="document__name">{{ document.name }}</span>
                }
                <span class="lty-badge document__badge">{{
                  typeLabel(document.type)
                }}</span>
                <span class="document__date">
                  <ng-container i18n>Date dépôt</ng-container> :
                  {{ shortDate(document.createdOn) }}
                </span>
                @if (document.letterDate; as letterDate) {
                  <span class="document__date">
                    <ng-container i18n>Date lettre</ng-container> :
                    {{ shortDate(letterDate) }}
                  </span>
                }
                @if (editable) {
                  <app-trash-icon
                    class="clickable document__remove"
                    height="16px"
                    width="16px"
                    color="var(--gray-400)"
                    role="button"
                    tabindex="0"
                    title="Supprimer"
                    i18n-title
                    (click)="confirmDelete(document)"
                    (keydown.enter)="confirmDelete(document)"
                    (keydown.space)="confirmDelete(document)"
                  />
                }
              </div>
            }
          </div>
        </app-card>
      } @else {
        <div class="mt-16 text-muted text-center" i18n>
          Aucun document à afficher
        </div>
      }
    </app-expansion-panel>

    <app-pdf-viewer-modal #pdfViewer />

    <app-action-modal
      #deleteModal
      [disabled]="submitting"
      (submitted)="remove()"
    >
      <ng-container mainTitle i18n>Supprimer ce document ?</ng-container>
      <ng-container subtitle i18n>Cette action est irréversible</ng-container>
      <ng-container submitButtonLabel i18n>Supprimer</ng-container>
    </app-action-modal>
  `,
  styles: `
    :host {
      display: block;
      width: 100%;
    }

    /* One line, everything at its natural width, 8px apart — the design's row. */
    .document {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .document > * {
      flex: 0 0 auto;
    }

    .document__name {
      min-width: 0;
      overflow: hidden;
      color: var(--gray-950);
      font-size: 14px;
      font-weight: 500;
      line-height: 24px;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* The row's only affordance, so the name says on hover that it opens. */
    .document__name--open:hover {
      text-decoration: underline;
    }

    /* The shared badge, at the row's smaller scale. */
    .document__badge {
      padding: 4px 8px;
      color: var(--light-gray-700);
      font-size: 10px;
      line-height: 12px;
      white-space: nowrap;
    }

    /* The row's last item, pushed away from the details it follows. */
    .document__remove {
      margin-left: auto;
    }

    .document__date {
      color: var(--light-gray-500);
      font-size: 10px;
      font-weight: 400;
      line-height: 12px;
      white-space: nowrap;
    }

    /* Narrow: the name keeps the first line, its details wrap under it. */
    @media screen and (max-width: 576px) {
      .document {
        flex-wrap: wrap;
      }

      .document__name {
        flex: 1 1 auto;
      }
    }
  `,
})
export class CancellationDocumentsComponent {
  private readonly detailsService = inject(CancellationDetailsService);

  /** « Ajouter »: the host owns the form, as it owns every other sheet. */
  readonly add = output<CancellationFlowDetailsResponse>();

  private readonly userAuthService = inject(UserAuthService);

  private readonly toastr = inject(ToastrService);

  private readonly cancellationService = inject(CancellationService);

  /** The request as it is once a document has gone: the host repaints from it. */
  readonly changed = output<CancellationFlowDetailsResponse>();

  @ViewChild('pdfViewer') private readonly viewer?: PdfViewerModalComponent;

  @ViewChild('deleteModal') private readonly deleteModal?: ActionModalComponent;

  private toRemove: CancellationFlowDocument | null = null;

  protected submitting = false;

  protected get documents(): CancellationFlowDocument[] {
    return this.detailsService.state.details?.documents ?? [];
  }

  /** A request that is over keeps the documents it ended with, and gains none. */
  protected get editable(): boolean {
    const { details } = this.detailsService.state;
    return !!details && !isCancellationFlowOver(details.status);
  }

  protected onAdd(): void {
    const { details } = this.detailsService.state;
    if (details && this.editable) this.add.emit(details);
  }

  protected confirmDelete(document: CancellationFlowDocument): void {
    this.toRemove = document;
    this.submitting = false;
    this.deleteModal?.openModal();
  }

  /** The file itself is kept; only this request forgets it, and the timeline says so. */
  protected remove(): void {
    const id = this.detailsService.state.details?.id;
    const document = this.toRemove;
    if (!id || !document || this.submitting) return;
    this.submitting = true;
    this.cancellationService.deleteDocument(id, document.documentId).subscribe({
      next: (res) => {
        this.submitting = false;
        this.toastr.success($localize`Document supprimé`);
        this.changed.emit(res.data);
        this.deleteModal?.closeModal();
      },
      error: (err) => {
        this.submitting = false;
        this.toastr.error(
          getApiErrorMessage(
            err,
            $localize`Une erreur est survenue. Veuillez réessayer`
          )
        );
      },
    });
  }

  protected typeLabel(type: CancellationDocumentType): string {
    return getCancellationDocumentTypeLabel(type);
  }

  protected shortDate(value: string): string {
    return fmtDate(value, SHORT_DATE);
  }

  protected open(document: CancellationFlowDocument): void {
    const reference = document.url;
    if (!reference) return;
    if (reference.includes('https://')) {
      this.show(reference);
      return;
    }
    this.userAuthService.getPresignedUrl(reference).subscribe({
      next: (presigned) => {
        if (presigned.status !== 'SUCCESS') {
          this.failed();
          return;
        }
        this.show(presigned.data.preSignedUrl);
      },
      error: () => this.failed(),
    });
  }

  private show(url: string): void {
    if (!this.viewer) return;
    this.viewer.pdfFileUrl = url;
    this.viewer.openModal();
  }

  private failed(): void {
    this.toastr.error($localize`Impossible d'ouvrir le document`);
  }
}
